import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
	Button,
	Progress,
	Tooltip,
	Typography,
	message,
	Modal,
	Table,
	Space,
	Tag,
	Input,
	Radio,
	Card,
	Col,
	Row,
	Statistic,
} from "antd";
import { CloudUploadOutlined, DownloadOutlined, CloseCircleOutlined, FileDoneOutlined, FolderOpenOutlined, PictureOutlined, WarningOutlined } from "@ant-design/icons";
import Utils from "../../../misc/Utils";

const { Text } = Typography;
const MAX_RETRIES = 2;
const MODE_UPLOAD_AND_IMPORT = "upload-and-import";
const MODE_EXCEL_ONLY = "excel-only";
const MODE_IMAGES_ONLY = "images-only";

export default function FolderUploadV2() {
	const excelInputRef = useRef(null);
	const folderInputRef = useRef(null);
	const uploadingRef = useRef(false);
	const cancelSourceRef = useRef(null);
	const cancelRequestedRef = useRef(false);

	const [previewSearch, setPreviewSearch] = useState("");
	const [canceling, setCanceling] = useState(false);

	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);

	const [excelFile, setExcelFile] = useState(null);
	const [selectedFolderFiles, setSelectedFolderFiles] = useState([]);
	const [selectedFolderLabel, setSelectedFolderLabel] = useState("");
	const [previewRows, setPreviewRows] = useState([]);
	const [excelOnlyPreviewRows, setExcelOnlyPreviewRows] = useState([]);
	const [selectedImportKeys, setSelectedImportKeys] = useState([]);
	const [processMode, setProcessMode] = useState(MODE_UPLOAD_AND_IMPORT);

	const [processModalVisible, setProcessModalVisible] = useState(false);

	const [summary, setSummary] = useState({
		totalSelected: 0,
		totalImages: 0,
		uploadedCount: 0,
		importCount: 0,
		unmatchedCount: 0,
		failedCount: 0,
		done: false,
	});

	const [logRows, setLogRows] = useState([]);
	const [logModalVisible, setLogModalVisible] = useState(false);

	const isUploadAndImportMode = processMode === MODE_UPLOAD_AND_IMPORT;
	const isExcelOnlyMode = processMode === MODE_EXCEL_ONLY;
	const isImagesOnlyMode = processMode === MODE_IMAGES_ONLY;

	// helper
	const isCanceledError = (err) => axios.isCancel?.(err) || err?.code === "ERR_CANCELED";

	const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

	const normalizeKey = (value) =>
		(value || "")
			.toString()
			.trim()
			.toLowerCase()
			.replace(/\.[^/.]+$/, "");

	const getBaseFilename = (name) => (name || "").replace(/\.[^/.]+$/, "");

	const isImageFile = (file) => {
		if (!file) return false;
		if (file.type && file.type.startsWith("image/")) return true;

		const name = (file.name || "").toLowerCase();
		return (
			name.endsWith(".jpg") ||
			name.endsWith(".jpeg") ||
			name.endsWith(".png") ||
			name.endsWith(".gif") ||
			name.endsWith(".webp") ||
			name.endsWith(".bmp") ||
			name.endsWith(".svg") ||
			name.endsWith(".heic") ||
			name.endsWith(".heif")
		);
	};

	const escapeCsv = (value) => {
		const text = (value ?? "").toString();
		if (text.includes(",") || text.includes('"') || text.includes("\n")) {
			return `"${text.replace(/"/g, '""')}"`;
		}
		return text;
	};

	const toComparableString = (value) => String(value ?? "").toLowerCase();

	const buildCsvText = (rows) => {
		const headers = [
			"status",
			"file_name",
			"file_base_name",
			"excel_image_value",
			"full_name",
			"reason",
			"error_message",
			"total_selected",
			"total_images",
			"processed",
		];

		return [
			headers.join(","),
			...rows.map((row) => headers.map((h) => escapeCsv(row[h])).join(",")),
		].join("\n");
	};

	const downloadCsvLog = (rows, filename = "student-pict-not-imported-and-failed.csv") => {
		if (!rows?.length) return;

		const csvText = buildCsvText(rows);
		const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
		const url = window.URL.createObjectURL(blob);

		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);

		window.URL.revokeObjectURL(url);
	};

	const handleDownloadUnmatchedPreview = () => {
		const imageUnmatchedRows = previewRows
			.filter((row) => !row.matched)
			.map((row, index) => ({
				status: "SKIPPED_NO_MATCH",
				file_name: row.file_name,
				file_base_name: row.file_base_name,
				excel_image_value: row.excel_image_value || "",
				full_name: row.full_name || "",
				reason: "No matching record in Excel by filename (without extension).",
				error_message: "",
				total_selected: previewRows.length,
				total_images: previewRows.length,
				processed: index + 1,
			}));

		const unmatchedRows = [...imageUnmatchedRows, ...excelOnlyPreviewRows].map((row, index) => ({
			...row,
			processed: index + 1,
		}));

		if (!unmatchedRows.length) return;

		const stamp = new Date().toISOString().replace(/[:.]/g, "-");
		downloadCsvLog(
			unmatchedRows,
			`student-pict-no-match-records-${stamp}.csv`
		);
	};

	const getAxiosErrorMessage = (err) => {
		if (!err) return "";
		const status = err?.response?.status;
		const statusText = err?.response?.statusText;
		const data = err?.response?.data;

		let serverText = "";
		if (typeof data === "string") serverText = data;
		else if (data && typeof data === "object") {
			try {
				serverText = JSON.stringify(data);
			} catch {
				serverText = "";
			}
		} else {
			serverText = err?.message || "";
		}

		return [status ? `${status}` : "", statusText || "", serverText]
			.filter(Boolean)
			.join(" | ");
	};

	const parseExcelToJson = async (file) => {
		const arrayBuffer = await file.arrayBuffer();
		const data = new Uint8Array(arrayBuffer);
		const workbook = XLSX.read(data, { type: "array" });

		const sheetName = workbook.SheetNames[0];
		if (!sheetName) throw new Error("Excel has no sheets.");

		const worksheet = workbook.Sheets[sheetName];
		const rows = XLSX.utils.sheet_to_json(worksheet);

		if (!rows?.length) throw new Error("Excel contains no rows.");
		return rows;
	};

	const buildExcelImageMap = (rows) => {
		const map = new Map();
		const duplicates = [];

		rows.forEach((row, index) => {
			const imageValue =
				row.id_number ??
				row.Id_number ??
				row.ID_NUMBER ??
				row["id_number "] ??
				row["Id_number "] ??
				"";

			const key = normalizeKey(imageValue);
			if (!key) return;

			if (map.has(key)) {
				duplicates.push({
					key,
					firstRowIndex: map.get(key).__rowIndex,
					duplicateRowIndex: index,
				});
				return;
			}

			map.set(key, { ...row, __rowIndex: index });
		});

		return { map, duplicates };
	};
	const getExcelImageValue = (row) =>
		row?.id_number ??
		row?.Id_number ??
		row?.ID_NUMBER ??
		row?.["id_number "] ??
		row?.["Id_number "] ??
		"";

	const getExcelFullName = (row) =>
		row?.full_name ??
		row?.Full_name ??
		row?.FULL_NAME ??
		"";

	const buildExcelNoPictureMatchRows = (
		excelImageMap,
		imageFiles,
		totalSelected,
		totalImages,
		processedOffset = 0
	) => {
		const imageKeySet = new Set(
			imageFiles
				.map((file) => normalizeKey(getBaseFilename(file.name)))
				.filter(Boolean)
		);

		return Array.from(excelImageMap.values())
			.filter((row) => {
				const excelKey = normalizeKey(getExcelImageValue(row));
				return excelKey && !imageKeySet.has(excelKey);
			})
			.map((row, index) => ({
				key: `excel-no-picture-${normalizeKey(getExcelImageValue(row)) || row.__rowIndex || index}`,
				status: "SKIPPED_NO_PICT_MATCH",
				file_name: "",
				file_base_name: "",
				excel_image_value: getExcelImageValue(row),
				full_name: getExcelFullName(row),
				reason: "No matching image file in selected folder by Excel ID number (without extension).",
				error_message: "",
				total_selected: totalSelected,
				total_images: totalImages,
				processed: processedOffset + index + 1,
			}));
	};

	const buildExcelOnlyPreviewRows = (rows) =>
		rows.map((row, index) => ({
			key: `excel-only-${row.__rowIndex ?? index}`,
			match_key: normalizeKey(getExcelImageValue(row)),
			file_name: "",
			file_base_name: "",
			excel_image_value: getExcelImageValue(row),
			full_name: getExcelFullName(row),
			matched: true,
			importMode: MODE_EXCEL_ONLY,
			sourceRow: row,
		}));

	const buildImagesOnlyPreviewRows = (imageFiles) =>
		imageFiles.map((file, index) => {
			const baseName = getBaseFilename(file.name);
			const key = normalizeKey(baseName);

			return {
				key: `image-only-${key || baseName || file.name}-${index}`,
				match_key: key,
				file_name: file.name,
				file_base_name: baseName,
				excel_image_value: "",
				full_name: "",
				matched: true,
				importMode: MODE_IMAGES_ONLY,
				sourceFile: file,
			};
		});

	const uploadSingleFile = async (file) => {
		const relativePath = (file.name || "")
			.replace(/\\/g, "/")
			.replace(/^\/+/, "");

		for (let retry = 1; retry <= MAX_RETRIES; retry++) {
			try {
				let uploadFile = file;
				const compressed = await Utils.advancedImageCompress(file);

				if (compressed) {
					if (compressed instanceof File) {
						uploadFile = compressed;
					} else {
						uploadFile = new File([compressed], file.name, {
							type: compressed.type || file.type || "image/jpeg",
							lastModified: Date.now(),
						});
					}
				}

				const formData = new FormData();
				formData.append("file", uploadFile, uploadFile.name || file.name);
				formData.append("relativePath", relativePath);
				formData.append("subdomain", localStorage.getItem("lms-subdomain") || "");
				formData.append("targetPath", process.env.REACT_APP_S3_PROFILE_PICTS || "");

				await axios.post(
					`${process.env.REACT_APP_API_S3_UPLOAD}/upload-folder-file`,
					formData,
					{ headers: { "Content-Type": "multipart/form-data" } }
				);

				return { success: true };
			} catch (err) {
				if (retry === MAX_RETRIES) return { success: false, error: err };
				await delay(1000 * retry);
			}
		}

		return { success: false };
	};

	// const importSingleExcelRow = async (row) => {
	// 	const payload = { ...row };
	// 	delete payload.__rowIndex;

	// 	await axios.post(
	// 		`${process.env.REACT_APP_API_MANAGEMENT}/student-pict-update`,
	// 		[payload],
	// 		{ headers: { "Content-Type": "application/json" } }
	// 	);
	// };
	const importSingleExcelRow = async (row) => {
		const payload = { ...row };
		delete payload.__rowIndex;

		const trimmedPayload = Object.fromEntries(
			Object.entries(payload).map(([key, value]) => [
				key,
				typeof value === "string" ? value.trim() : value,
			])
		);

		await axios.post(
			`${process.env.REACT_APP_API_MANAGEMENT}/student-pict-update`,
			[trimmedPayload],
			{ headers: { "Content-Type": "application/json" } }
		);
	};

	const resetSelection = () => {
		setExcelFile(null);
		setSelectedFolderFiles([]);
		setSelectedFolderLabel("");
		resetPreparedState();
		hasSummary && setSummary((prev) => ({ ...prev, done: false }));
		if (excelInputRef.current) excelInputRef.current.value = "";
		if (folderInputRef.current) folderInputRef.current.value = "";
	};

	const resetPreparedState = () => {
		setPreviewRows([]);
		setExcelOnlyPreviewRows([]);
		setSelectedImportKeys([]);
	};

	// cancel action
	const handleCancelProcess = () => {
		if (!uploadingRef.current) return;
		cancelRequestedRef.current = true;
		setCanceling(true);
		cancelSourceRef.current?.cancel("User canceled processing.");
	};

	const handleExcelSelected = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setExcelFile(file);
		resetPreparedState();
		message.success(`Excel selected: ${file.name}`);
	};

	const handleFolderSelected = async (e) => {
		if (uploadingRef.current) return;
		const files = Array.from(e.target.files || []);
		if (!files.length) return;

		const folderNameRaw =
			files[0]?.webkitRelativePath?.split("/")?.[0] || "Selected folder";
		const folderName = folderNameRaw || "Selected folder";
		setSelectedFolderFiles(files);
		setSelectedFolderLabel(`${folderName} (${files.length} files)`);
		resetPreparedState();

		message.success(`Folder selected: ${folderName} (${files.length} files)`);
	};

	const handlePrepareRecords = async () => {
		if (isExcelOnlyMode && !excelFile) {
			message.warning("Please select an Excel file first.");
			return;
		}

		if (isUploadAndImportMode && (!excelFile || !selectedFolderFiles.length)) {
			message.warning("Please select both Excel file and image folder first.");
			return;
		}

		if (isImagesOnlyMode && !selectedFolderFiles.length) {
			message.warning("Please select an image folder first.");
			return;
		}

		try {
			if (isExcelOnlyMode) {
				const allRows = await parseExcelToJson(excelFile);
				const { duplicates } = buildExcelImageMap(allRows);

				if (duplicates.length) {
					message.warning(
						`Found ${duplicates.length} duplicate image values in Excel. First occurrence per key will be used.`
					);
				}

				const rows = buildExcelOnlyPreviewRows(allRows);
				const sortedRows = [...rows].sort((a, b) =>
					(a.full_name || "").localeCompare(b.full_name || "", undefined, {
						sensitivity: "base",
						numeric: true,
					})
				);

				setPreviewRows(sortedRows);
				setExcelOnlyPreviewRows([]);
				message.success(`Loaded ${rows.length} Excel records for selection.`);
				return;
			}

			if (isImagesOnlyMode) {
				const imageFiles = selectedFolderFiles.filter(isImageFile);
				const rows = buildImagesOnlyPreviewRows(imageFiles);
				setPreviewRows(rows);
				setExcelOnlyPreviewRows([]);
				message.success(`Loaded ${rows.length} image records for upload.`);
				return;
			}

			const allRows = await parseExcelToJson(excelFile);
			const { map: excelImageMap, duplicates } = buildExcelImageMap(allRows);

			if (duplicates.length) {
				message.warning(
					`Found ${duplicates.length} duplicate image values in Excel. First occurrence per key will be used.`
				);
			}

			const imageFiles = selectedFolderFiles.filter(isImageFile);
			const rows = imageFiles.map((file, index) => {
				const baseName = getBaseFilename(file.name);
				const key = normalizeKey(baseName);
				const matchedRow = excelImageMap.get(key);
				const excelValue = matchedRow ? getExcelImageValue(matchedRow) : "";

				return {
					key: `${key || baseName || file.name}-${index}`,
					match_key: key,
					file_name: file.name,
					file_base_name: baseName,
					excel_image_value: excelValue,
					full_name: getExcelFullName(matchedRow),
					matched: !!matchedRow,
					importMode: MODE_UPLOAD_AND_IMPORT,
				};
			});

			const sortedRows = [...rows].sort((a, b) =>
				(a.full_name || "").localeCompare(b.full_name || "", undefined, {
					sensitivity: "base",
					numeric: true,
				})
			);

			setPreviewRows(sortedRows);
			setExcelOnlyPreviewRows(
				buildExcelNoPictureMatchRows(
					excelImageMap,
					imageFiles,
					imageFiles.length,
					imageFiles.length,
					rows.filter((row) => !row.matched).length
				)
			);

			message.success(`Loaded ${rows.length} image records for selection.`);
		} catch (err) {
			message.error(err?.message || "Failed to prepare records.");
		}
	};

	const handleStartProcessing = async () => {
		if (uploadingRef.current) return;

		if (isExcelOnlyMode && !excelFile) {
			message.warning("Please select an Excel file first.");
			return;
		}

		if (isUploadAndImportMode && (!excelFile || !selectedFolderFiles.length)) {
			message.warning("Please select both Excel file and image folder first.");
			return;
		}

		if (isImagesOnlyMode && !selectedFolderFiles.length) {
			message.warning("Please select an image folder first.");
			return;
		}

		uploadingRef.current = true;
		setUploading(true);
		setProgress(0);
		setSummary({
			totalSelected: selectedFolderFiles.length,
			totalImages: 0,
			uploadedCount: 0,
			importCount: 0,
			unmatchedCount: 0,
			failedCount: 0,
			done: false,
		});

		cancelRequestedRef.current = false;
		setCanceling(false);
		cancelSourceRef.current = axios.CancelToken.source();

		try {
			if (isExcelOnlyMode) {
				const selectedRows = previewRows
					.filter((row) => selectedImportKeys.includes(row.key))
					.map((row) => row.sourceRow)
					.filter(Boolean);

				if (!selectedRows.length) {
					throw new Error("No selected records to import.");
				}

				let importCount = 0;
				let failedCount = 0;
				const excludedLogs = [];

				for (let index = 0; index < selectedRows.length; index++) {
					if (cancelRequestedRef.current) {
						throw new axios.Cancel("Canceled by user.");
					}

					const row = selectedRows[index];
					const excelImageValue = getExcelImageValue(row);
					const fullName = getExcelFullName(row);

					try {
						await importSingleExcelRow(row);
						importCount++;
					} catch (err) {
						failedCount++;
						excludedLogs.push({
							key: `excel-only-${row.__rowIndex ?? index}-import-fail`,
							status: "IMPORT_FAILED",
							file_name: "",
							file_base_name: "",
							excel_image_value: excelImageValue,
							full_name: fullName,
							reason: "Excel import failed.",
							error_message: getAxiosErrorMessage(err) || err?.message || "",
							total_selected: selectedRows.length,
							total_images: 0,
							processed: index + 1,
						});
					}

					setProgress(Math.round(((index + 1) / selectedRows.length) * 100));
				}

				setSummary({
					totalSelected: selectedRows.length,
					totalImages: 0,
					uploadedCount: 0,
					importCount,
					unmatchedCount: 0,
					failedCount,
					done: true,
				});

				setLogRows(excludedLogs);
				if (excludedLogs.length > 0) {
					setLogModalVisible(true);
				}

				return;
			}

			if (isImagesOnlyMode) {
				const selectedRows = previewRows
					.filter((row) => selectedImportKeys.includes(row.key))
					.map((row) => row.sourceFile)
					.filter(Boolean);

				if (!selectedRows.length) {
					throw new Error("No selected images to upload.");
				}

				let uploadedCount = 0;
				let failedCount = 0;
				const excludedLogs = [];

				for (let index = 0; index < selectedRows.length; index++) {
					if (cancelRequestedRef.current) {
						throw new axios.Cancel("Canceled by user.");
					}

					const file = selectedRows[index];
					const baseName = getBaseFilename(file.name);
					const uploadResult = await uploadSingleFile(file);

					if (!uploadResult.success) {
						failedCount++;
						excludedLogs.push({
							key: `${file.name}-${index}-upload-only-fail`,
							status: "UPLOAD_FAILED",
							file_name: file.name,
							file_base_name: baseName,
							excel_image_value: "",
							full_name: "",
							reason: "Image upload failed.",
							error_message:
								getAxiosErrorMessage(uploadResult?.error) ||
								uploadResult?.error?.message ||
								"",
							total_selected: selectedRows.length,
							total_images: selectedRows.length,
							processed: index + 1,
						});
					} else {
						uploadedCount++;
					}

					setProgress(Math.round(((index + 1) / selectedRows.length) * 100));
				}

				setSummary({
					totalSelected: selectedRows.length,
					totalImages: selectedRows.length,
					uploadedCount,
					importCount: 0,
					unmatchedCount: 0,
					failedCount,
					done: true,
				});

				setLogRows(excludedLogs);
				if (excludedLogs.length > 0) {
					setLogModalVisible(true);
				}

				return;
			}

			const allRows = await parseExcelToJson(excelFile);
			const { map: excelImageMap, duplicates } = buildExcelImageMap(allRows);

			if (duplicates.length) {
				message.warning(
					`Found ${duplicates.length} duplicate image values in Excel. First occurrence per key will be used.`
				);
			}

			const totalSelected = selectedFolderFiles.length;
			const allImageFiles = selectedFolderFiles.filter(isImageFile);
			const selectedKeySet = new Set(selectedImportKeys);
			const imageFiles = allImageFiles.filter((file, index) => {
				const baseName = getBaseFilename(file.name);
				const key = normalizeKey(baseName);
				const rowKey = `${key || baseName || file.name}-${index}`;
				return selectedKeySet.has(rowKey);
			});
			const totalImages = imageFiles.length;

			if (!totalImages) {
				throw new Error("No selected records to import.");
			}

			const imageNoMatchLogs = previewRows
				.filter((row) => !row.matched)
				.map((row, index) => ({
					key: `${row.file_name}-${index}-preview-no-match`,
					status: "SKIPPED_NO_MATCH",
					file_name: row.file_name,
					file_base_name: row.file_base_name,
					excel_image_value: row.excel_image_value || "",
					full_name: row.full_name || "",
					reason: "No matching record in Excel by filename (without extension).",
					error_message: "",
					total_selected: totalSelected,
					total_images: allImageFiles.length,
					processed: index + 1,
				}));
			const excelNoPictureLogs = buildExcelNoPictureMatchRows(
				excelImageMap,
				allImageFiles,
				totalSelected,
				allImageFiles.length,
				imageNoMatchLogs.length
			);

			let uploadedCount = 0;
			let importCount = 0;
			let unmatchedCount = imageNoMatchLogs.length + excelNoPictureLogs.length;
			let failedCount = 0;

			const excludedLogs = [...imageNoMatchLogs, ...excelNoPictureLogs];
			let processed = excludedLogs.length;
			let processedSelected = 0;

			for (const file of imageFiles) {
				if (cancelRequestedRef.current) {
					throw new axios.Cancel("Canceled by user.");
				}

				processed += 1;
				processedSelected += 1;
				const baseName = getBaseFilename(file.name);
				const key = normalizeKey(baseName);
				const matchedRow = excelImageMap.get(key);

				if (!matchedRow) {
					unmatchedCount++;
					excludedLogs.push({
						key: `${file.name}-${processed}-no-match`,
						status: "SKIPPED_NO_MATCH",
						file_name: file.name,
						file_base_name: baseName,
						excel_image_value: "",
						full_name: "",
						reason: "No matching record in Excel by filename (without extension).",
						error_message: "",
						total_selected: totalSelected,
						total_images: totalImages,
						processed,
					});
					setProgress(Math.round((processedSelected / imageFiles.length) * 100));
					continue;
				}

				const uploadResult = await uploadSingleFile(file);
				if (!uploadResult.success) {
					if (uploadResult.canceled) {
						throw uploadResult.error || new axios.Cancel("Canceled by user.");
					}
					failedCount++;
					excludedLogs.push({
						key: `${file.name}-${processed}-upload-fail`,
						status: "UPLOAD_FAILED",
						file_name: file.name,
						file_base_name: baseName,
						excel_image_value: getExcelImageValue(matchedRow),
						full_name: getExcelFullName(matchedRow),
						reason: "Image upload failed.",
						error_message:
							getAxiosErrorMessage(uploadResult?.error) ||
							uploadResult?.error?.message ||
							"",
						total_selected: totalSelected,
						total_images: totalImages,
						processed,
					});
					setProgress(Math.round((processedSelected / imageFiles.length) * 100));
					continue;
				}

				uploadedCount++;

				try {
					await importSingleExcelRow(matchedRow);
					importCount++;
				} catch (err) {
					failedCount++;
					excludedLogs.push({
						key: `${file.name}-${processed}-import-fail`,
						status: "IMPORT_FAILED",
						file_name: file.name,
						file_base_name: baseName,
						excel_image_value: getExcelImageValue(matchedRow),
						full_name: getExcelFullName(matchedRow),
						reason: "Image uploaded, but import API failed.",
						error_message: getAxiosErrorMessage(err) || err?.message || "",
						total_selected: totalSelected,
						total_images: totalImages,
						processed,
					});
				}

				setProgress(Math.round((processedSelected / imageFiles.length) * 100));
			}

			setSummary({
				totalSelected,
				totalImages,
				uploadedCount,
				importCount,
				unmatchedCount,
				failedCount,
				done: true,
			});

			setLogRows(excludedLogs);
			if (excludedLogs.length > 0) {
				setLogModalVisible(true);
			}
		} catch (err) {
			if (isCanceledError(err)) {
				message.info("Processing canceled.");
			} else {
				message.error(err?.message || "Process failed.");
			}
		} finally {
			uploadingRef.current = false;
			setUploading(false);
			setCanceling(false);
			cancelSourceRef.current = null;
			setProgress((prev) => (prev < 100 ? prev : 100));
		}
	};

	const openMainProcessModal = () => {
		setProcessModalVisible(true);
	};

	const closeMainProcessModal = () => {
		if (uploadingRef.current) return;
		setProcessModalVisible(false);
	};

	const logColumns = [
		{ title: "Status", dataIndex: "status", key: "status", width: 170 },
		{ title: "File Name", dataIndex: "file_name", key: "file_name", width: 220 },
		{ title: "File Base", dataIndex: "file_base_name", key: "file_base_name", width: 180 },
		{
			title: "Excel Image Value",
			dataIndex: "excel_image_value",
			key: "excel_image_value",
			width: 180,
		},
		{ title: "Full Name", dataIndex: "full_name", key: "full_name", width: 220 },
		{ title: "Reason", dataIndex: "reason", key: "reason", width: 280 },
		{ title: "Error", dataIndex: "error_message", key: "error_message", width: 320 },
		{ title: "Processed", dataIndex: "processed", key: "processed", width: 100 },
	];

	const canStart =
		((isExcelOnlyMode || isUploadAndImportMode) ? !!excelFile : true) &&
		((isImagesOnlyMode || isUploadAndImportMode) ? selectedFolderFiles.length > 0 : true) &&
		!uploading;
	const hasSummary = summary.done;
	const matchedPreviewCount = previewRows.filter((row) => row.matched).length;
	const unmatchedPreviewCount = previewRows.length - matchedPreviewCount;
	const excelOnlyPreviewCount = excelOnlyPreviewRows.length;
	const unmatchedReportCount = unmatchedPreviewCount + excelOnlyPreviewCount;
	const selectedCount = selectedImportKeys.length;
	const resultModeLabel = isUploadAndImportMode
		? "Upload + Import Completed"
		: isExcelOnlyMode
		? "Excel Import Completed"
		: "Image Upload Completed";
	const resultAccent = isUploadAndImportMode
		? "linear-gradient(135deg, #1677ff 0%, #0052d5 100%)"
		: isExcelOnlyMode
		? "linear-gradient(135deg, #003fa4 0%, #36cfc9 100%)"
		: "linear-gradient(135deg, #1d39c4 0%, #69b1ff 100%)";
	const resultCards = [
		{
			key: "selected",
			title: "Selected",
			value: summary.totalSelected,
			icon: <FolderOpenOutlined style={{ color: "#1677ff", fontSize: 18 }} />,
			bg: "#f0f5ff",
		},
		{
			key: "images",
			title: "Images",
			value: summary.totalImages,
			icon: <PictureOutlined style={{ color: "#722ed1", fontSize: 18 }} />,
			bg: "#f9f0ff",
		},
		{
			key: "uploaded",
			title: "Uploaded",
			value: summary.uploadedCount,
			icon: <CloudUploadOutlined style={{ color: "#1677ff", fontSize: 18 }} />,
			bg: "#e6f0ff",
		},
		{
			key: "imported",
			title: "Imported",
			value: summary.importCount,
			icon: <FileDoneOutlined style={{ color: "#389e0d", fontSize: 18 }} />,
			bg: "#f6ffed",
		},
		{
			key: "skipped",
			title: "Skipped",
			value: summary.unmatchedCount,
			icon: <WarningOutlined style={{ color: "#d48806", fontSize: 18 }} />,
			bg: "#fffbe6",
		},
		{
			key: "failed",
			title: "Failed",
			value: summary.failedCount,
			icon: <CloseCircleOutlined style={{ color: "#cf1322", fontSize: 18 }} />,
			bg: "#fff1f0",
		},
	];

	const previewColumns = [
		{
			title: "Full Name",
			dataIndex: "full_name",
			key: "full_name",
			width: 240,
			render: (value, record) => {
				if (value) return value;
				if (record.importMode === MODE_IMAGES_ONLY) {
					return <Text type="secondary">Image only</Text>;
				}
				return <Text type="secondary">-</Text>;
			},
		},
		{
			title: "Id Number",
			dataIndex: "excel_image_value",
			key: "excel_image_value",
			width: 130,
			render: (value, record) => {
				if (value) return value;
				if (record.importMode === MODE_IMAGES_ONLY) {
					return <Text type="secondary">Not used</Text>;
				}
				return <Text type="secondary">No match</Text>;
			},
		},
		{
			title: "Image File",
			dataIndex: "file_name",
			key: "file_name",
			width: 180,
			render: (value, record) =>
				value ||
				(record.importMode === MODE_EXCEL_ONLY ? (
					<Text type="secondary">Skipped</Text>
				) : (
					<Text type="secondary">-</Text>
				)),
		},
		{
			title: "Status",
			dataIndex: "matched",
			key: "matched",
			width: 120,
			render: (matched, record) => {
				if (record.importMode === MODE_EXCEL_ONLY) {
					return <Tag color="blue">Excel Only</Tag>;
				}

				if (record.importMode === MODE_IMAGES_ONLY) {
					return <Tag color="gold">Image Only</Tag>;
				}

				return matched ? <Tag color="green">Matched</Tag> : <Tag color="red">No Match</Tag>;
			},
		},
	];

	const filteredPreviewRows = useMemo(() => {
		const q = previewSearch.trim().toLowerCase();
		if (!q) return previewRows;

		return previewRows.filter((row) => {
			const fileName = toComparableString(row.file_name);
			const fileBase = toComparableString(row.file_base_name);
			const excelValue = toComparableString(row.excel_image_value);
			const fullName = toComparableString(row.full_name);
			return (
				fileName.includes(q) ||
				fileBase.includes(q) ||
				excelValue.includes(q) ||
				fullName.includes(q)
			);
		});
	}, [previewRows, previewSearch]);

	return (
		<>
			<input
				ref={excelInputRef}
				type="file"
				accept=".xlsx,.xls,.csv"
				hidden
				onChange={handleExcelSelected}
			/>

			<input
				ref={folderInputRef}
				type="file"
				multiple
				webkitdirectory="true"
				directory="true"
				accept="image/*"
				hidden
				onChange={handleFolderSelected}
			/>

			<Tooltip title="Upload Student Picts">
				<Button
					className="management-create-btn button-shadow"
					block
					type="primary"
					size="large"
					shape="round"
					icon={<CloudUploadOutlined />}
					onClick={openMainProcessModal}
				>
					<Text style={{ color: "#fff" }}>Upload Student Picts</Text>
				</Button>
			</Tooltip>

			<Modal
				title="Upload Student Picts"
				open={processModalVisible}
				onCancel={closeMainProcessModal}
				width={900}
				maskClosable={!uploading}
				closable={!uploading}
				footer={
					<Space>
						<Button onClick={resetSelection} disabled={uploading}>
							Clear Selection
						</Button>
						<Button onClick={closeMainProcessModal} disabled={uploading}>
							Close
						</Button>
					</Space>
				}
			>
				<Space direction="vertical" style={{ width: "100%" }} size={14}>
					<Space direction="vertical" style={{ width: "100%" }} size={10}>
						<Radio.Group
							value={processMode}
							onChange={(e) => {
								setProcessMode(e.target.value);
								resetPreparedState();
							}}
							disabled={uploading}
						>
							<Space wrap>
								<Radio.Button value={MODE_UPLOAD_AND_IMPORT}>Upload + Import</Radio.Button>
								<Radio.Button value={MODE_EXCEL_ONLY}>Import Excel Only</Radio.Button>
								<Radio.Button value={MODE_IMAGES_ONLY}>Upload Images Only</Radio.Button>
							</Space>
						</Radio.Group>

						<Space wrap>
							<div className="management-action-row">
								<div className="management-action-group">
									<Button className='button-shadow' type='primary' shape='round' onClick={() => excelInputRef.current?.click()} disabled={uploading || isImagesOnlyMode}>
										Select Import File
									</Button>

									<Button className='button-shadow' type='primary' shape='round' onClick={() => folderInputRef.current?.click()} disabled={uploading || isExcelOnlyMode}>
										Select Folder
									</Button>

									<Button className='button-shadow' type='primary' shape='round' onClick={handlePrepareRecords} disabled={uploading || !canStart}>
										Load Records
									</Button>
								</div>

								<Button
									className='button-shadow'
									shape='round'
									type={uploading ? "default" : "primary"}
									danger={uploading}
									onClick={uploading ? handleCancelProcess : handleStartProcessing}
									disabled={uploading ? canceling : !canStart || selectedCount === 0}
									loading={canceling}
								>
									{uploading ? "Cancel Processing" : "Start Processing"}
								</Button>
								<Button
									size="small"
									icon={<DownloadOutlined />}
									onClick={handleDownloadUnmatchedPreview}
									disabled={uploading || unmatchedReportCount === 0 || !isUploadAndImportMode}
								>
									Download No Match CSV
								</Button>
							</div>
						</Space>
					</Space>

					{previewRows.length > 0 && (
						<>
							<Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
								<Space wrap>
									<Text>
										{isExcelOnlyMode ? "Ready to Import: " : isImagesOnlyMode ? "Ready to Upload: " : "Matched: "}<Text strong>{matchedPreviewCount}</Text>
									</Text>
									{isUploadAndImportMode && (
										<Text>
											No Match: <Text strong>{unmatchedPreviewCount}</Text>
										</Text>
									)}
									{isUploadAndImportMode && (
										<Text>
											Excel Without Picture: <Text strong>{excelOnlyPreviewCount}</Text>
										</Text>
									)}
									<Text>
										Selected: <Text strong>{selectedCount}</Text>
									</Text>
									<Button
										size="small"
										onClick={() =>
											setSelectedImportKeys(
												previewRows.filter((row) => row.matched).map((row) => row.key)
											)
										}
										disabled={uploading}
									>
										{isUploadAndImportMode ? "Select All Matched" : "Select All"}
									</Button>
									<Button
										size="small"
										onClick={() => setSelectedImportKeys([])}
										disabled={uploading}
									>
										Clear Selection
									</Button>
									
								</Space>

								<Input
									allowClear
									placeholder="Search file name, ID, or full name"
									value={previewSearch}
									onChange={(e) => setPreviewSearch(e.target.value)}
									style={{ width: 360, borderRadius: 7 }}
									disabled={uploading}
								/>
							</Space>

							<Table
								rowKey="key"
								columns={previewColumns}
								dataSource={filteredPreviewRows}
								size="small"
								scroll={{ x: 800, y: 320 }}
								pagination={{ pageSize: 8, showSizeChanger: false }}
								// pagination={false}
								rowSelection={{
									selectedRowKeys: selectedImportKeys,
									onChange: (keys, selectedRows) => {
										setSelectedImportKeys(
											keys.filter((key) =>
												selectedRows.find((row) => row.key === key && row.matched)
											)
										);
									},
									getCheckboxProps: (record) => ({
										disabled: !record.matched || uploading,
									}),
								}}
							/>
						</>
					)}

					<div>
						<div style={{ marginBottom: 4 }}>
							<Text strong>Import File: </Text>
							{excelFile ? (
								<Tag color="blue">{excelFile.name}</Tag>
							) : (
								<Text type="secondary">{isImagesOnlyMode ? "Optional in images-only mode" : "Not selected"}</Text>
							)}
						</div>

						<div>
							<Text strong>Folder: </Text>
							{selectedFolderLabel ? (
								<Tag color="purple">{selectedFolderLabel}</Tag>
							) : (
								<Text type="secondary">{isExcelOnlyMode ? "Optional in Excel-only mode" : "Not selected"}</Text>
							)}
						</div>
					</div>

					{uploading && <Progress percent={progress} status="active" />}

					{hasSummary && (
						<div
							style={{
								marginTop: 8,
								padding: 14,
								borderRadius: 16,
								background: "#ffffff",
								border: "1px solid #edf2f7",
								boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
							}}
						>
							<div
								style={{
									padding: 14,
									borderRadius: 12,
									background: resultAccent,
									color: "#fff",
									marginBottom: 12,
								}}
							>
								<div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
									<div>
										<div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, opacity: 0.82 }}>
											Processing Result
										</div>
										<div style={{ fontSize: 19, fontWeight: 700, marginTop: 3 }}>
											{resultModeLabel}
										</div>
										<div style={{ marginTop: 4, opacity: 0.9 }}>
											{summary.failedCount === 0
												? "All selected records finished without failures."
												: `${summary.failedCount} record${summary.failedCount > 1 ? "s" : ""} need attention.`}
										</div>
									</div>
									<div style={{ textAlign: "right", minWidth: 148 }}>
										<div style={{ fontSize: 11, opacity: 0.82 }}>Success Snapshot</div>
										<div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.05, marginTop: 2 }}>
											{isImagesOnlyMode ? summary.uploadedCount : summary.importCount}
										</div>
										<div style={{ marginTop: 4, opacity: 0.9 }}>
											{isImagesOnlyMode ? "Images Uploaded" : isExcelOnlyMode ? "Rows Imported" : "Records Fully Imported"}
										</div>
									</div>
								</div>
							</div>

							<Row gutter={[10, 10]}>
								{resultCards.map((card) => (
									<Col xs={12} sm={8} lg={8} key={card.key}>
										<Card
											bodyStyle={{ padding: 12 }}
											style={{
												borderRadius: 12,
												border: "1px solid #edf2f7",
												boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
											}}
										>
											<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
												<Text type="secondary" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.7 }}>
													{card.title}
												</Text>
												<div
													style={{
														width: 36,
														height: 36,
														borderRadius: 12,
														background: card.bg,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}
												>
													{card.icon}
												</div>
											</div>
											<Statistic value={card.value} valueStyle={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }} />
										</Card>
									</Col>
								))}
							</Row>
						</div>
					)}
				</Space>
			</Modal>

			<Modal
				title="Skipped and Failed Records"
				open={logModalVisible}
				onCancel={() => setLogModalVisible(false)}
				width={1200}
				footer={
					<Space>
						<Button onClick={() => setLogModalVisible(false)}>Close</Button>
						<Button
							type="primary"
							icon={<DownloadOutlined />}
							onClick={() => {
								const stamp = new Date().toISOString().replace(/[:.]/g, "-");
								downloadCsvLog(logRows, `student-pict-skipped-and-failed-${stamp}.csv`);
							}}
							disabled={!logRows.length}
						>
							Download CSV
						</Button>
					</Space>
				}
			>
				<Table
					columns={logColumns}
					dataSource={logRows}
					size="small"
					scroll={{ x: 1600, y: 420 }}
					// pagination={{ pageSize: 10, showSizeChanger: true }}
				/>
			</Modal>
		</>
	);
}

